import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import { useReportGenerate } from '@/hooks';
import { FileType } from '@/hooks/useFileLoading';
import { Checkbox, Col, Form, Modal, ModalProps, Row, Tooltip } from 'antd';
import { useForm } from 'antd/lib/form/Form';

interface Props extends Omit<ModalProps, 'onCancel'> {
  onCancel: VoidFunction;
  workOrderId?: number;
  complementId?: number;
  format?: FileType;
  workorderNumber?: number;
  licensePlate?: string;
  complementIdentifier?: string;
}

interface FormValues {
  hideHourCost?: boolean;
  showCustomerObservation?: boolean;
  hideFinancialValues?: boolean;
  printDiscountValuesItem?: boolean;
  hidePartsAndServicesCode?: boolean;
}

export default function WorkOrderReportAdministrativeSyntheticModal({
  onCancel,
  workOrderId,
  complementId,
  format,
  licensePlate,
  complementIdentifier,
  ...props
}: Props) {
  const [form] = useForm<FormValues>();

  const hideFinancialValues = Form.useWatch('hideFinancialValues', form);

  const reportAdminSynthetic = useCommonGenerateReport(
    ``,
    'Relatório gerado com sucesso!',
    'Relatório administrativo - Sintético gerado com sucesso.',
  );

  const report = useReportGenerate();

  function handleCancel() {
    onCancel();
    form.resetFields();
  }

  const handleOk = async () => {
    const values = await form.validateFields();

    if (!!workOrderId) {
      const result = await reportAdminSynthetic.mutateAsync({
        workOrderId: workOrderId,
        complementId,
        format: 'PDF',
        ...values,
        url: '/generate/work-order-synthetic',
      });

      const fileName = `relatorio-administrativo-sintetico${
        complementIdentifier ? `-${complementIdentifier}` : ''
      }${licensePlate ? `-${licensePlate}` : ''}.pdf`;

      if (format === 'PDF') {
        report.downloadFile({
          result,
          fileName,
        });
      } else {
        report.openFile({
          result,
          fileName,
          fileType: 'application/pdf',
        });
      }

      handleCancel();
    }
  };

  return (
    <Modal
      width={800}
      title="Relatório administrativo - Sintético"
      okText="Gerar relatório"
      confirmLoading={reportAdminSynthetic.isLoading}
      zIndex={2000}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnClose
      {...props}
    >
      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Col span={24} md={12} xl={8}>
            <Tooltip
              title="Ao selecionar essa opção, os campos 'Valor Unitário' e 'Valor Líquido' serão ocultados no relatório."
              zIndex={2001}
            >
              <Form.Item name="hideHourCost" valuePropName="checked" noStyle>
                <Checkbox>Ocultar detalhes dos valores</Checkbox>
              </Form.Item>
            </Tooltip>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="showCustomerObservation"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Exibir observações do cliente</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="hideFinancialValues"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Ocultar valores do orçamento</Checkbox>
            </Form.Item>
          </Col>

          {!hideFinancialValues && (
            <Col span={24} md={12} xl={8}>
              <Form.Item
                name="printDiscountValuesItem"
                valuePropName="checked"
                noStyle
              >
                <Checkbox>Exibir descontos em $</Checkbox>
              </Form.Item>
            </Col>
          )}

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="hidePartsAndServicesCode"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Não exibir códigos de peças e serviços</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
